import { DatePipe } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { ScholarshipApplicationApiService } from '../../../core/api-services/scholarship-application-api.service';
import { VerificationDocument } from '../../../core/models/verification-document';
import { ConfirmationService } from '../../../core/services/confirmation';
import { PaginationComponent } from '../../../shared/pagination/pagination';

@Component({
  selector: 'app-admin-documents',
  imports: [DatePipe, FormsModule, PaginationComponent],
  templateUrl: './documents.html',
  styleUrl: './documents.scss',
})
export class AdminDocumentsComponent implements OnInit {
  private readonly api = inject(ScholarshipApplicationApiService);
  private readonly confirmation = inject(ConfirmationService);
  readonly documents = signal<VerificationDocument[]>([]);
  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly downloading = signal<number | null>(null);
  readonly error = signal('');
  readonly message = signal('');
  readonly status = signal('All');
  readonly search = signal('');
  readonly page = signal(1);
  readonly pageSize = 10;
  readonly pendingCount = computed(() => this.documents().filter(d => d.reviewStatus === 'Pending').length);
  readonly filtered = computed(() => {
    const query = this.search().trim().toLowerCase();
    return this.documents().filter(d => (this.status() === 'All' || d.reviewStatus === this.status())
      && `${d.name} ${d.applicantName} ${d.referenceNumber} ${d.scholarshipTitle} ${d.documentType}`.toLowerCase().includes(query));
  });
  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filtered().length / this.pageSize)));
  readonly pageDocuments = computed(() => this.filtered().slice((this.page() - 1) * this.pageSize, this.page() * this.pageSize));
  readonly showingFrom = computed(() => this.filtered().length ? (this.page() - 1) * this.pageSize + 1 : 0);
  readonly showingTo = computed(() => Math.min(this.page() * this.pageSize, this.filtered().length));

  ngOnInit(): void { this.load(); }

  load(): void {
    if (this.loading() || this.busy()) return;
    this.loading.set(true);
    this.error.set('');
    this.message.set('');
    this.api.getDocumentsForVerification().pipe(finalize(() => this.loading.set(false))).subscribe({
      next: documents => { this.documents.set(documents); this.page.set(1); },
      error: () => this.error.set('Documents could not be loaded. Please try Refresh.'),
    });
  }

  async review(document: VerificationDocument, decision: 'Approved' | 'Rejected'): Promise<void> {
    if (this.busy() || this.loading() || document.reviewStatus === decision) return;
    this.busy.set(true);
    this.error.set('');
    this.message.set('');
    const action = decision === 'Approved' ? 'Approve' : 'Reject';
    const confirmed = await this.confirmation.open({
      title: `${action} document?`,
      message: `${action} ${document.name} for ${document.applicantName} (${document.referenceNumber})?`,
      confirmText: action, cancelText: 'Cancel', type: decision === 'Approved' ? 'success' : 'danger',
    });
    if (!confirmed) { this.busy.set(false); return; }
    this.api.reviewDocument(document.applicationId, document.id, decision)
      .pipe(finalize(() => this.busy.set(false))).subscribe({
        next: review => {
          this.documents.update(documents => documents.map(item => {
            if (item.applicationId !== document.applicationId) return item;
            const updated = review.documents.find(d => d.id === item.id);
            return updated ? { ...item, ...updated } : item;
          }));
          this.page.set(Math.min(this.page(), this.totalPages()));
          this.message.set(`${document.name} ${decision.toLowerCase()}.`);
        },
        error: () => this.error.set('The document decision could not be saved. Please try again.'),
      });
  }

  download(document: VerificationDocument): void {
    if (this.downloading() !== null) return;
    this.downloading.set(document.id);
    this.error.set('');
    this.api.downloadApplicationDocument(document.applicationId, document.id)
      .pipe(finalize(() => this.downloading.set(null))).subscribe({
        next: blob => {
          const url = URL.createObjectURL(blob);
          const link = window.document.createElement('a');
          link.href = url;
          link.download = document.name;
          link.click();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        },
        error: () => this.error.set('The document could not be downloaded. Please try again.'),
      });
  }
}
